/* eslint-disable react-hooks/set-state-in-effect -- effects reset/sync browser state on url and pending-navigation changes */
import { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Globe, ArrowLeft, ArrowRight, RefreshCw, Home, Bookmark, BookmarkCheck, X, Monitor, MonitorX, Plus } from 'lucide-react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDesktop } from '@/desktop/DesktopContext';
import { useAppContext } from '@/hooks/useAppContext';

export interface NsiteBookmark {
  url: string;
  title: string;
  favicon?: string;
  addedAt: number;
}

const HOME_URL = 'https://nsite.lol';

/** One browser tab — owns its own URL, navigation history and fetched metadata. */
interface BrowserTab {
  id: string;
  url: string;
  inputUrl: string;
  history: string[];
  historyIndex: number;
  /** Bumped to force the iframe to remount (reload), independent of the URL. */
  reloadKey: number;
  pageTitle: string;
  pageFavicon: string;
}

let tabCounter = 0;

function createTab(url: string = HOME_URL): BrowserTab {
  return {
    id: `tab-${++tabCounter}`,
    url,
    inputUrl: url,
    history: [url],
    historyIndex: 0,
    reloadKey: 0,
    pageTitle: '',
    pageFavicon: '',
  };
}

/** A tab is "pristine" when it's still sitting on the untouched home page. */
function isPristineHome(tab: BrowserTab): boolean {
  return tab.url === HOME_URL && tab.history.length === 1 && tab.historyIndex === 0;
}

/**
 * Last-resort favicon via Google's service. Used only when we can't find
 * an icon in the page HTML itself.
 */
function googleFaviconUrl(url: string): string {
  try {
    const { hostname } = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=64`;
  } catch {
    return '';
  }
}

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

interface PageMeta {
  title: string;
  favicon: string;
}

/** Resolve an href (possibly relative) against a base URL. */
function resolveUrl(href: string, base: string): string {
  try { return new URL(href, base).href; } catch { return href; }
}

/**
 * Fetch a page's HTML (direct first, then via CORS proxy) and extract
 * title + best favicon. For SPAs with no static <title>, falls back to:
 *   1. og:title / application-name meta tags
 *   2. The web app manifest (link rel="manifest") name + icons
 */
async function fetchPageMeta(pageUrl: string): Promise<PageMeta> {
  const attempts = [pageUrl, `${CORS_PROXY}${encodeURIComponent(pageUrl)}`];

  for (const attempt of attempts) {
    try {
      const res = await fetch(attempt, { signal: AbortSignal.timeout(6000) });
      if (!res.ok) continue;
      const html = await res.text();

      // --- Title: <title> first, then og:title, then application-name ---
      let title = html.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim() ?? '';
      if (!title) {
        title = html.match(/<meta[^>]+property=["']og:title["'][^>]+content=["']([^"']+)["']/i)?.[1]?.trim()
          ?? html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+property=["']og:title["']/i)?.[1]?.trim()
          ?? html.match(/<meta[^>]+name=["']application-name["'][^>]+content=["']([^"']+)["']/i)?.[1]?.trim()
          ?? html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+name=["']application-name["']/i)?.[1]?.trim()
          ?? '';
      }

      // --- Favicon: apple-touch-icon → shortcut icon → icon ---
      const iconPatterns = [
        /rel=["']apple-touch-icon["'][^>]*href=["']([^"']+)["']/i,
        /rel=["']shortcut icon["'][^>]*href=["']([^"']+)["']/i,
        /rel=["']icon["'][^>]*href=["']([^"']+)["']/i,
        /href=["']([^"']+)["'][^>]*rel=["']apple-touch-icon["']/i,
        /href=["']([^"']+)["'][^>]*rel=["']shortcut icon["']/i,
        /href=["']([^"']+)["'][^>]*rel=["']icon["']/i,
      ];
      let favicon = '';
      for (const pattern of iconPatterns) {
        const m = html.match(pattern);
        if (m?.[1]) { favicon = resolveUrl(m[1], pageUrl); break; }
      }

      // --- Manifest fallback for SPAs (title + high-res icon) ---
      const manifestHref = html.match(/rel=["']manifest["'][^>]*href=["']([^"']+)["']/i)?.[1]
        ?? html.match(/href=["']([^"']+)["'][^>]*rel=["']manifest["']/i)?.[1];

      if (manifestHref && (!title || !favicon)) {
        const manifestUrl = resolveUrl(manifestHref, pageUrl);
        const proxiedManifest = `${CORS_PROXY}${encodeURIComponent(manifestUrl)}`;
        for (const mUrl of [manifestUrl, proxiedManifest]) {
          try {
            const mr = await fetch(mUrl, { signal: AbortSignal.timeout(4000) });
            if (!mr.ok) continue;
            const manifest = await mr.json() as { name?: string; short_name?: string; icons?: { src: string; sizes?: string }[] };
            if (!title) title = (manifest.name ?? manifest.short_name ?? '').trim();
            if (!favicon && manifest.icons?.length) {
              // Pick largest icon
              const sorted = [...manifest.icons].sort((a, b) => {
                const sizeA = parseInt(a.sizes?.split('x')[0] ?? '0', 10);
                const sizeB = parseInt(b.sizes?.split('x')[0] ?? '0', 10);
                return sizeB - sizeA;
              });
              favicon = resolveUrl(sorted[0].src, pageUrl);
            }
            break;
          } catch { /* try next */ }
        }
      }

      if (title || favicon) return { title, favicon };
    } catch { /* try next attempt */ }
  }

  // Final fallback
  let fallbackTitle: string;
  try { fallbackTitle = new URL(pageUrl).hostname; } catch { fallbackTitle = pageUrl.slice(0, 30); }
  return { title: fallbackTitle, favicon: '' };
}

function normalizeNsiteUrl(input: string): string {
  let url = input.trim();
  if (!url) return '';
  if (url.startsWith('npub1')) {
    return `https://npub.to/${url}`;
  }
  if (!url.startsWith('http')) {
    url = `https://${url}`;
  }
  return url;
}

/** Derive a short friendly label from a URL hostname. */
function hostLabel(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url.slice(0, 30);
  }
}

/**
 * Renders a single tab's iframe. Every open tab keeps its iframe mounted so
 * page state (scroll, sessions, forms) survives tab switches — inactive tabs
 * are just hidden. Also owns the per-tab metadata fetch.
 */
function TabFrame({
  tab,
  active,
  onMeta,
}: {
  tab: BrowserTab;
  active: boolean;
  onMeta: (tabId: string, url: string, meta: PageMeta) => void;
}) {
  // Fetch real page title + favicon whenever this tab navigates.
  useEffect(() => {
    let cancelled = false;
    fetchPageMeta(tab.url).then((meta) => {
      if (!cancelled) onMeta(tab.id, tab.url, meta);
    });
    return () => { cancelled = true; };
  }, [tab.id, tab.url, onMeta]);

  return (
    <iframe
      key={tab.reloadKey}
      src={tab.url}
      className="absolute inset-0 w-full h-full border-0"
      style={{ display: active ? 'block' : 'none' }}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
      title={tab.pageTitle || 'Nsite Browser'}
    />
  );
}

export function NsiteBrowserApp() {
  const [tabs, setTabs] = useState<BrowserTab[]>(() => [createTab()]);
  const [activeTabId, setActiveTabId] = useState<string>(() => tabs[0].id);

  const { pendingNsiteUrl, clearPendingNsiteUrl } = useDesktop();
  const { config, updateConfig } = useAppContext();

  const [bookmarks, setBookmarks] = useLocalStorage<NsiteBookmark[]>(
    'nostrvm:nsite-bookmarks',
    [
      { url: 'https://nsite.lol', title: 'nsite.lol', addedAt: 0 },
      { url: 'https://npub.to', title: 'npub.to', addedAt: 0 },
    ],
    { serialize: JSON.stringify, deserialize: JSON.parse }
  );

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
  const { url, inputUrl, pageTitle, pageFavicon, history, historyIndex } = activeTab;

  const isBookmarked = bookmarks.some((b) => b.url === url);
  const isShortcut = (config.desktopShortcuts ?? []).some((s) => s.url === url);

  /** Apply a partial update (or updater) to a single tab by id. */
  const patchTab = useCallback((id: string, update: Partial<BrowserTab> | ((t: BrowserTab) => BrowserTab)) => {
    setTabs((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        return typeof update === 'function' ? update(t) : { ...t, ...update };
      })
    );
  }, []);

  // Store fetched metadata on the originating tab, then patch any stale
  // bookmark / desktop shortcut that was saved with bad data. Stable identity
  // so TabFrame's effect doesn't re-run on every render.
  const handleMeta = useCallback((tabId: string, metaUrl: string, meta: PageMeta) => {
    const title = meta.title;
    const favicon = meta.favicon || googleFaviconUrl(metaUrl);

    setTabs((prev) =>
      prev.map((t) =>
        // Only apply if the tab is still on the URL we fetched for.
        t.id === tabId && t.url === metaUrl ? { ...t, pageTitle: title, pageFavicon: favicon } : t
      )
    );

    setBookmarks((prev) =>
      prev.map((b) => {
        if (b.url !== metaUrl) return b;
        const needsTitle = !b.title || b.title === new URL(metaUrl).hostname;
        const needsFavicon = !b.favicon;
        if (!needsTitle && !needsFavicon) return b;
        return { ...b, title: title || b.title, favicon: favicon || b.favicon };
      })
    );

    updateConfig((cfg) => ({
      ...cfg,
      desktopShortcuts: (cfg.desktopShortcuts ?? []).map((s) => {
        if (s.url !== metaUrl) return s;
        const needsTitle = !s.title || s.title === new URL(metaUrl).hostname;
        const needsFavicon = !s.favicon;
        if (!needsTitle && !needsFavicon) return s;
        return { ...s, title: title || s.title, favicon: favicon || s.favicon };
      }),
    }));
  }, [setBookmarks, updateConfig]);

  // --- Tab management ---

  const openTab = useCallback((rawUrl: string) => {
    const normalized = normalizeNsiteUrl(rawUrl);
    const tab = createTab(normalized);
    setTabs((prev) => [...prev, tab]);
    setActiveTabId(tab.id);
  }, []);

  const newTab = useCallback(() => openTab(HOME_URL), [openTab]);

  const closeTab = useCallback((id: string) => {
    if (tabs.length === 1) {
      // Never leave the browser empty — replace the last tab with a fresh home tab.
      const fresh = createTab();
      setTabs([fresh]);
      setActiveTabId(fresh.id);
      return;
    }
    const idx = tabs.findIndex((t) => t.id === id);
    const next = tabs.filter((t) => t.id !== id);
    setTabs(next);
    // If we closed the active tab, focus its neighbour.
    if (activeTabId === id) {
      setActiveTabId(next[Math.min(idx, next.length - 1)].id);
    }
  }, [tabs, activeTabId]);

  // Open pendingNsiteUrl (from a shortcut double-click or a Feed/Articles link)
  // in a new tab. If the browser was just opened and only shows a pristine home
  // tab, reuse that tab instead of stacking an extra blank one.
  useEffect(() => {
    if (!pendingNsiteUrl) return;
    const normalized = normalizeNsiteUrl(pendingNsiteUrl);
    if (tabs.length === 1 && isPristineHome(tabs[0])) {
      const reused: BrowserTab = {
        ...tabs[0],
        url: normalized,
        inputUrl: normalized,
        history: [normalized],
        historyIndex: 0,
        reloadKey: tabs[0].reloadKey + 1,
        pageTitle: '',
        pageFavicon: '',
      };
      setTabs([reused]);
      setActiveTabId(reused.id);
    } else {
      const tab = createTab(normalized);
      setTabs((prev) => [...prev, tab]);
      setActiveTabId(tab.id);
    }
    clearPendingNsiteUrl();
  }, [pendingNsiteUrl, clearPendingNsiteUrl, tabs]);

  // --- Navigation (operates on the active tab) ---

  const navigate = useCallback((newUrl: string) => {
    const normalized = normalizeNsiteUrl(newUrl);
    patchTab(activeTabId, (t) => {
      const trimmed = t.history.slice(0, t.historyIndex + 1);
      return {
        ...t,
        url: normalized,
        inputUrl: normalized,
        history: [...trimmed, normalized],
        historyIndex: t.historyIndex + 1,
        reloadKey: t.reloadKey + 1,
        pageTitle: '',
        pageFavicon: '',
      };
    });
  }, [activeTabId, patchTab]);

  const goBack = () => {
    patchTab(activeTabId, (t) => {
      if (t.historyIndex <= 0) return t;
      const newIndex = t.historyIndex - 1;
      return { ...t, historyIndex: newIndex, url: t.history[newIndex], inputUrl: t.history[newIndex], reloadKey: t.reloadKey + 1 };
    });
  };

  const goForward = () => {
    patchTab(activeTabId, (t) => {
      if (t.historyIndex >= t.history.length - 1) return t;
      const newIndex = t.historyIndex + 1;
      return { ...t, historyIndex: newIndex, url: t.history[newIndex], inputUrl: t.history[newIndex], reloadKey: t.reloadKey + 1 };
    });
  };

  const refresh = () => patchTab(activeTabId, (t) => ({ ...t, reloadKey: t.reloadKey + 1 }));

  const goHome = () => navigate(HOME_URL);

  const setInputUrl = (value: string) => patchTab(activeTabId, { inputUrl: value });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(inputUrl);
  };

  /** Build a bookmark for the active tab using its live title + favicon (or fallbacks). */
  const buildBookmark = async (): Promise<NsiteBookmark> => {
    let title = pageTitle;
    let favicon = pageFavicon;
    if (!title || !favicon) {
      const meta = await fetchPageMeta(url);
      if (!title) title = meta.title;
      if (!favicon) favicon = meta.favicon;
    }
    // Final fallback for favicon
    if (!favicon) favicon = googleFaviconUrl(url);
    return {
      url,
      title: title || hostLabel(url),
      favicon,
      addedAt: Date.now(),
    };
  };

  const toggleBookmark = async () => {
    if (isBookmarked) {
      setBookmarks((prev) => prev.filter((b) => b.url !== url));
      updateConfig((cfg) => ({
        ...cfg,
        desktopShortcuts: (cfg.desktopShortcuts ?? []).filter((s) => s.url !== url),
      }));
    } else {
      const newBookmark = await buildBookmark();
      setBookmarks((prev) => [...prev, newBookmark]);
    }
  };

  const removeBookmark = (bookmarkUrl: string) => {
    setBookmarks((prev) => prev.filter((b) => b.url !== bookmarkUrl));
    updateConfig((cfg) => ({
      ...cfg,
      desktopShortcuts: (cfg.desktopShortcuts ?? []).filter((s) => s.url !== bookmarkUrl),
    }));
  };

  const toggleShortcut = async () => {
    if (isShortcut) {
      updateConfig((cfg) => ({
        ...cfg,
        desktopShortcuts: (cfg.desktopShortcuts ?? []).filter((s) => s.url !== url),
      }));
    } else {
      const shortcut = await buildBookmark();
      // Ensure the page is also bookmarked
      if (!isBookmarked) {
        setBookmarks((prev) => [...prev, shortcut]);
      }
      updateConfig((cfg) => ({
        ...cfg,
        desktopShortcuts: [...(cfg.desktopShortcuts ?? []), shortcut],
      }));
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tab Bar */}
      <div className="flex items-stretch gap-0.5 px-1 pt-1 border-b border-border/50 bg-muted/40 overflow-x-auto">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              role="button"
              tabIndex={0}
              onClick={() => setActiveTabId(tab.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveTabId(tab.id); }}
              title={tab.url}
              className={`group flex items-center gap-1.5 h-8 pl-2 pr-1 rounded-t-md cursor-pointer text-xs select-none shrink-0 max-w-[180px] border-b-2 transition-colors ${
                isActive
                  ? 'bg-background border-primary'
                  : 'bg-muted/40 border-transparent hover:bg-muted/70'
              }`}
            >
              {tab.pageFavicon ? (
                <img
                  src={tab.pageFavicon}
                  alt=""
                  className="w-3.5 h-3.5 shrink-0 rounded-sm object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <Globe className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate flex-1">{tab.pageTitle || hostLabel(tab.url)}</span>
              <button
                className="w-4 h-4 shrink-0 rounded-sm flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground opacity-60 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                title="Close tab"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          );
        })}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 self-center" onClick={newTab}>
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">New tab</TooltipContent>
        </Tooltip>
      </div>

      {/* Address Bar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border/50 bg-muted/30">
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={goBack} disabled={historyIndex <= 0}>
          <ArrowLeft className="w-3.5 h-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={goForward} disabled={historyIndex >= history.length - 1}>
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={refresh}>
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={goHome}>
          <Home className="w-3.5 h-3.5" />
        </Button>
        <form onSubmit={handleSubmit} className="flex-1 flex gap-1">
          <div className="relative flex-1">
            {/* Show fetched favicon in address bar when available */}
            {pageFavicon ? (
              <img
                src={pageFavicon}
                alt=""
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-sm object-contain pointer-events-none"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <Globe className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            )}
            <Input
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              className="h-7 pl-7 text-xs"
              placeholder="Enter URL or npub..."
            />
          </div>
        </form>

        {/* Bookmark toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className={`h-7 w-7 shrink-0 ${isBookmarked ? 'text-amber-500' : ''}`}
              onClick={toggleBookmark}
            >
              {isBookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {isBookmarked ? 'Remove bookmark' : 'Bookmark this page'}
          </TooltipContent>
        </Tooltip>

        {/* Desktop shortcut toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className={`h-7 w-7 shrink-0 ${isShortcut ? 'text-sky-500' : ''}`}
              onClick={toggleShortcut}
            >
              {isShortcut ? <MonitorX className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {isShortcut ? 'Remove desktop shortcut' : 'Add desktop shortcut'}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Bookmarks Bar */}
      {bookmarks.length > 0 && (
        <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border/50 bg-muted/20 overflow-x-auto">
          {bookmarks.map((bookmark) => (
            <div key={bookmark.url} className="relative group flex items-center shrink-0">
              <button
                className="flex items-center gap-1 h-6 px-2 rounded text-xs hover:bg-muted/60 transition-colors max-w-[120px]"
                onClick={() => navigate(bookmark.url)}
                title={bookmark.url}
              >
                {bookmark.favicon ? (
                  <img
                    src={bookmark.favicon}
                    alt=""
                    className="w-3 h-3 shrink-0 rounded-sm object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <Globe className="w-3 h-3 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate">{bookmark.title}</span>
                {/* Small monitor badge if also a desktop shortcut */}
                {(config.desktopShortcuts ?? []).some((s) => s.url === bookmark.url) && (
                  <Monitor className="w-2.5 h-2.5 text-sky-500 shrink-0" />
                )}
              </button>
              <button
                className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-muted-foreground/20 hover:bg-destructive hover:text-destructive-foreground hidden group-hover:flex items-center justify-center transition-colors"
                onClick={() => removeBookmark(bookmark.url)}
                title="Remove bookmark"
              >
                <X className="w-2 h-2" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* iframes — every tab stays mounted; only the active one is shown */}
      <div className="relative flex-1 bg-white">
        {tabs.map((tab) => (
          <TabFrame
            key={tab.id}
            tab={tab}
            active={tab.id === activeTabId}
            onMeta={handleMeta}
          />
        ))}
      </div>
    </div>
  );
}
